import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismBadge,
  PrismButton,
  PrismComboSelectInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import type { ReservationFilter } from 'lumen/models';
import {
  isSupportedReservationFilterId,
  RESERVATION_FILTER_CONFIG,
  type ReservationFilterId,
  type ReservationFilterOption,
} from 'lumen/models/reservation-filter';

import styles from './dropdown-filter.module.css';

export type PropertyTagsActiveFilters = Partial<
  Record<ReservationFilterId, string[]>
>;

interface FilterSectionData {
  hasSelection: boolean;
  key: ReservationFilterId;
  label: string;
  options: ReservationFilterOption[];
  selected: ReservationFilterOption[];
}

interface Signature {
  Args: {
    activeFilters: PropertyTagsActiveFilters;
    isLoading?: boolean;
    onApply: (filters: PropertyTagsActiveFilters) => void;
    reservationFilters: ReservationFilter[];
  };
}

// Must be unique across the entire app — used to detect outside clicks
// that should close the dropdown (see #handleOutsideClick).
const DROPDOWN_CLASS = 'property-tags-filter-dropdown';

export default class SettingsPropertyTagsDropdownFilter extends Component<Signature> {
  @service declare intl: Services['intl'];

  @tracked isOpen = false;
  @tracked pendingFilters: PropertyTagsActiveFilters = {};

  handleOutsideClick = (event: MouseEvent): void => {
    if (
      this.isOpen &&
      event.target instanceof Element &&
      event.target.closest(`.${DROPDOWN_CLASS}`) === null
    ) {
      this.cancel();
    }
  };
  styles = styles;

  get activeFiltersCount(): number {
    return Object.values(this.args.activeFilters).filter((v) => v.length > 0)
      .length;
  }

  @cached
  get filterSectionsData(): FilterSectionData[] {
    return this.args.reservationFilters
      .filter((filter) => isSupportedReservationFilterId(filter.id as string))
      .map((filter) => {
        const key = filter.id as ReservationFilterId;
        const pending = this.pendingFilters[key] ?? [];

        return {
          key,
          label: this.intl.t(RESERVATION_FILTER_CONFIG[key].labelKey),
          options: filter.options,
          selected: filter.options.filter((o) => pending.includes(o.id)),
          hasSelection: pending.length > 0,
        };
      });
  }

  get hasError(): boolean {
    return !this.args.isLoading && this.filterSectionsData.length === 0;
  }

  get hasPendingChanges(): boolean {
    const active = this.args.activeFilters;
    const pending = this.pendingFilters;
    const allKeys = new Set([
      ...(Object.keys(active) as ReservationFilterId[]),
      ...(Object.keys(pending) as ReservationFilterId[]),
    ]);
    for (const key of allKeys) {
      const aVals = [...(active[key] ?? [])].sort();
      const pVals = [...(pending[key] ?? [])].sort();
      if (aVals.join('\x00') !== pVals.join('\x00')) {
        return true;
      }
    }
    return false;
  }

  get isApplyDisabled(): boolean {
    return !this.hasPendingChanges;
  }

  get isDisabled(): boolean {
    return this.args.isLoading || this.filterSectionsData.length === 0;
  }

  willDestroy(): void {
    super.willDestroy();
    document.removeEventListener('click', this.handleOutsideClick);
  }

  @action
  applyFilters(): void {
    this.args.onApply({ ...this.pendingFilters });
    this.isOpen = false;
  }

  @action
  attachOutsideClickHandler(): void {
    document.addEventListener('click', this.handleOutsideClick);
  }

  @action
  cancel(): void {
    this.pendingFilters = { ...this.args.activeFilters };
    this.isOpen = false;
  }

  @action
  clearAll(): void {
    this.pendingFilters = {};
  }

  @action
  clearSection(key: ReservationFilterId, event: MouseEvent): void {
    event.stopPropagation();

    const filters = { ...this.pendingFilters };
    delete filters[key];
    this.pendingFilters = filters;
  }

  @action
  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.cancel();
    }
  }

  @action
  openDropdown(): void {
    this.pendingFilters = { ...this.args.activeFilters };
    this.isOpen = !this.isOpen;
  }

  @action
  setMultiSelected(
    key: ReservationFilterId,
    options: ReservationFilterOption | ReservationFilterOption[],
  ): void {
    const arr = Array.isArray(options) ? options : [options];
    this.pendingFilters = {
      ...this.pendingFilters,
      [key]: arr.map((o) => o.id),
    };
  }

  <template>
    <div
      class={{concat this.styles.container " " DROPDOWN_CLASS}}
      {{didInsert this.attachOutsideClickHandler}}
    >
      <PrismButton
        @buttonType="secondary"
        @iconBefore="filter"
        @isDisabled={{this.isDisabled}}
        @label={{t "general.filter"}}
        @onClick={{this.openDropdown}}
        aria-expanded={{if this.isOpen "true" "false"}}
        aria-haspopup="true"
        data-test-id="dropdown-property-tags-filter"
      >
        {{#if this.activeFiltersCount}}
          <PrismBadge
            @isSmall={{true}}
            @label={{this.activeFiltersCount}}
            @type="info"
          />
        {{/if}}
      </PrismButton>
      {{#if this.hasError}}
        <PrismTooltip
          @side="bottom"
          @text={{t "reservations.filters.error_tooltip"}}
        />
      {{/if}}

      {{#if this.isOpen}}
        {{! template-lint-disable no-invalid-interactive }}
        <div
          aria-label={{t "general.filter"}}
          class={{this.styles.dropdown}}
          data-test-id="property-tags-filter-dropdown"
          role="group"
          {{on "keydown" this.handleKeydown}}
        >
          <div class={{this.styles.body}}>
            {{#each this.filterSectionsData as |section|}}
              <div
                class={{this.styles.column}}
                data-test-id={{concat "filter-section-" section.key}}
              >
                <div class="flex justify-between">
                  <p class="text-200--bold">{{section.label}}</p>
                  <button
                    aria-label={{concat
                      (t "settings.central_mapping.filters.clear")
                      " "
                      section.label
                    }}
                    class="link-button--unstyled prism-link prism-link--small prism-link--standalone"
                    disabled={{not section.hasSelection}}
                    type="button"
                    {{on "click" (fn this.clearSection section.key)}}
                  >
                    {{t "settings.central_mapping.filters.clear"}}
                  </button>
                </div>
                <PrismComboSelectInput
                  @isSearchable={{true}}
                  @onChange={{fn this.setMultiSelected section.key}}
                  @options={{section.options}}
                  @unsafe_isMultipleImmediate={{true}}
                  @unsafe_renderInPlace={{false}}
                  @value={{section.selected}}
                />
              </div>
            {{/each}}
          </div>

          <div class={{this.styles.footer}}>
            <div>
              <PrismButton
                @buttonType="ghost"
                @label={{t "settings.central_mapping.filters.clear_all"}}
                @onClick={{this.clearAll}}
                data-test-id="clear-all-filters"
              />
            </div>
            <div class="flex gap-200">
              <PrismButton
                @buttonType="secondary"
                @label={{t "general.cancel"}}
                @onClick={{this.cancel}}
                data-test-id="cancel-filters"
              />
              <PrismButton
                @isDisabled={{this.isApplyDisabled}}
                @label={{t "general.apply"}}
                @onClick={{this.applyFilters}}
                data-test-id="apply-filters"
              />
            </div>
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
